import { useState } from "react";
import { MapPin, Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import api, { formatApiError } from "../lib/api";

const SUBJECTS = ["Contact général", "Rejoindre le club", "Partenariat", "Projet Sénégal", "Autre"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé ! Le club vous répondra prochainement.");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-navy/20 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-steel focus:outline-none focus:border-rouge transition-colors duration-300";

  return (
    <>
      <SEO title="Contact" description="Contactez le Club Cycliste Le Boulou : rejoindre le club, partenariats, projet Sénégal." />
      <PageHeader kicker="Parlons vélo" title="Contact" />

      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_420px] gap-12">
          <Reveal>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block font-heading uppercase tracking-wider text-xs text-navy mb-2">Nom complet</label>
                  <input id="contact-name" data-testid="contact-name" required value={form.name} onChange={set("name")} placeholder="Votre nom" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block font-heading uppercase tracking-wider text-xs text-navy mb-2">Email</label>
                  <input id="contact-email" data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.fr" className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block font-heading uppercase tracking-wider text-xs text-navy mb-2">Sujet</label>
                <select id="contact-subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} className={inputCls}>
                  {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="block font-heading uppercase tracking-wider text-xs text-navy mb-2">Message</label>
                <textarea id="contact-message" data-testid="contact-message" required rows={7} value={form.message} onChange={set("message")} placeholder="Votre message…" className={inputCls} />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-9 py-4 hover:bg-navy disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? "Envoi…" : "Envoyer le message"}
                <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="space-y-5">
              <div className="bg-navy text-white p-8">
                <p className="font-heading uppercase tracking-wider text-sm text-rouge mb-6">Coordonnées</p>
                <ul className="space-y-4 text-sm text-white/70">
                  <li className="flex items-start gap-3"><MapPin size={16} className="text-rouge mt-0.5 shrink-0" /><span>1 avenue du Stade, Complexe des Échards<br />66160 Le Boulou</span></li>
                  <li className="flex items-center gap-3"><Mail size={16} className="text-rouge shrink-0" /><a href="mailto:ccleboulou@live.fr" data-testid="contact-email-link" className="hover:text-rouge transition-colors">ccleboulou@live.fr</a></li>
                  <li className="flex items-center gap-3"><Phone size={16} className="text-rouge shrink-0" /><a href="tel:+33782854699" data-testid="contact-phone-link" className="hover:text-rouge transition-colors">07 82 85 46 99</a></li>
                </ul>
              </div>
              <div className="border border-navy/10 overflow-hidden" data-testid="contact-map">
                <iframe
                  title="Carte du Boulou"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=2.78%2C42.49%2C2.89%2C42.56&layer=mapnik&marker=42.5246%2C2.8304"
                  className="w-full h-[300px] border-0"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
